/**
 * Side nav structure (#939, Feature C). Config-driven so adding a section (MCP Servers,
 * My Connections) is a one-line change here, and so the active-item mapping is unit-testable
 * without rendering AppContent.
 */

import type { Route } from "./routing";

export type NavItemId = "skills" | "mcp" | "myskills" | "following" | "contribute" | "admin" | "help";
export type NavIcon = "grid" | "server" | "user" | "users" | "plus" | "sliders" | "help";

export interface NavItem {
  id: NavItemId;
  label: string;
  icon: NavIcon;
  /** Shown only to Marketplace admins (the same check that gates the Admin page). */
  adminOnly?: boolean;
  /** Shown only when the MCP Servers section is on (REACT_APP_MCP_SERVERS_ENABLED). */
  mcpServersOnly?: boolean;
  /** Shown only when the Following section is on (REACT_APP_FOLLOWING_ENABLED). */
  followingOnly?: boolean;
}

export interface NavVisibility {
  isAdmin: boolean;
  mcpServersEnabled: boolean;
  followingEnabled?: boolean;
}

export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Discover",
    items: [
      { id: "skills", label: "Skills & Plugins", icon: "grid" },
      { id: "mcp", label: "MCP Servers", icon: "server", mcpServersOnly: true },
    ],
  },
  {
    label: "Yours",
    items: [
      { id: "myskills", label: "My Skills", icon: "user" },
      { id: "following", label: "Following", icon: "users", followingOnly: true },
      { id: "contribute", label: "Contribute", icon: "plus" },
    ],
  },
  { label: "Platform tools", items: [{ id: "admin", label: "Admin", icon: "sliders", adminOnly: true }] },
  { label: "Help", items: [{ id: "help", label: "Help", icon: "help" }] },
];

/** The groups this user should see. Hidden items are removed, not shown disabled; a group left empty is dropped. */
export function visibleNavGroups(
  { isAdmin, mcpServersEnabled, followingEnabled = false }: NavVisibility,
  groups: readonly NavGroup[] = NAV_GROUPS,
): NavGroup[] {
  const visible = (item: NavItem) =>
    (!item.adminOnly || isAdmin) &&
    (!item.mcpServersOnly || mcpServersEnabled) &&
    (!item.followingOnly || followingEnabled);
  return groups
    .map((group) => ({ ...group, items: group.items.filter(visible) }))
    .filter((group) => group.items.length > 0);
}

/** Which nav item a route belongs to, so the section stays highlighted on its detail and edit pages too. */
export function activeNavItemId(route: Route): NavItemId {
  switch (route.page) {
    case "catalog":
    case "detail":
      return "skills";
    case "myskills":
      return "myskills";
    case "following":
      return "following";
    case "edit":
      // Editing opened from My Skills belongs to My Skills; otherwise it's part of Contribute.
      return route.from === "myskills" ? "myskills" : "contribute";
    case "contribute":
      return "contribute";
    case "admin":
      return "admin";
    case "help":
      return "help";
    case "mcp":
    case "mcpserver":
      return "mcp";
    default: {
      // Exhaustiveness guard: a new Route variant must map to a nav item here.
      const _exhaustive: never = route;
      return _exhaustive;
    }
  }
}
